import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Boxes, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import TableExportButton from "@/components/TableExportButton";

export default function IndentLog() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const load = () => {
    api.get("/indent-log").then((r) => setRows(r.data));
    setSelected([]);
  };
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, []);

  const filtered = rows.filter((r) =>
    !q ||
    r.description.toLowerCase().includes(q.toLowerCase()) ||
    (r.item_code || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.category || "").toLowerCase().includes(q.toLowerCase())
  );

  const rowKey = (r) => r.item_code || r.description || `__sn_${r.sn}`;

  const toggle = (k) =>
    setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleAll = (ks) =>
    setSelected((s) => (ks.every((k) => s.includes(k)) ? s.filter((x) => !ks.includes(x)) : Array.from(new Set([...s, ...ks]))));

  const remove = async (r) => {
    const key = r.item_code || r.description || "—";
    const confirmMsg = `Delete "${r.description}" (${r.item_code || "no code"}) from the Store Log?\n\n` +
      `This will also remove matching line items from Store Entries and Issue Slips. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.delete(`/indent-log/${encodeURIComponent(key)}`);
      toast.success(`Removed · ${data.store_entries_touched} receipts & ${data.issue_slips_touched} slips touched`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected entr${selected.length === 1 ? "y" : "ies"}? This cascades through Store Entries and Issue Slips.`)) return;
    let touched = 0;
    for (const key of selected) {
      try {
        const { data } = await api.delete(`/indent-log/${encodeURIComponent(key)}`);
        touched += (data.store_entries_touched || 0) + (data.issue_slips_touched || 0);
      } catch (err) { /* keep going */ }
    }
    toast.success(`Bulk removed ${selected.length} item(s) (${touched} cascade ops)`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Inventory</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Store Log</h1>
          <p className="text-sm text-slate-500 mt-1">Live inventory: Received − Issued = Balance.</p>
        </div>
        <Boxes size={28} className="text-slate-400" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-2 flex-wrap">
        <Search size={14} className="text-slate-500 ml-1" />
        <input data-testid="indent-log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by item, code or category…" className="flex-1 border-0 outline-none text-sm bg-transparent py-1.5 min-w-[200px]" />
        <span className="text-xs text-slate-500 font-mono">{filtered.length} items</span>
        <TableExportButton
          testIdPrefix="indent-log-export"
          filename="inventory_stock_log"
          rows={filtered.map((r) => ({
            item_code: r.item_code || "",
            description: r.description || "",
            category: r.category || "",
            cost_centers: r.cost_centers || "",
            unit: r.unit || "",
            received: r.received_qty ?? 0,
            issued: r.issued_qty ?? 0,
            balance: (Number(r.received_qty || 0) - Number(r.issued_qty || 0)),
            reorder_level: r.reorder_level ?? "",
            last_updated: (r.last_updated || "").slice(0, 10),
          }))}
          dateField="last_updated"
          columns={[
            { key: "item_code", label: "Item Code" },
            { key: "description", label: "Description" },
            { key: "category", label: "Category" },
            { key: "cost_centers", label: "Cost Centre" },
            { key: "unit", label: "Unit" },
            { key: "received", label: "Received Qty" },
            { key: "issued", label: "Issued Qty" },
            { key: "balance", label: "Balance" },
            { key: "reorder_level", label: "Reorder Level" },
            { key: "last_updated", label: "Last Updated" },
          ]}
        />
      </div>

      {canManage && selected.length > 0 && (
        <div className="bg-[#1D2951] text-white rounded-md px-4 py-2 flex items-center justify-between shadow-md" data-testid="store-log-bulk-bar">
          <div className="text-sm font-semibold tracking-tight">{selected.length} selected</div>
          <div className="flex gap-2">
            <button onClick={() => setSelected([])} className="text-xs px-3 py-1.5 rounded-md hover:bg-white/10">Clear</button>
            <button onClick={bulkDelete} data-testid="store-log-bulk-confirm" className="bg-rose-600 hover:bg-rose-700 text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1"><Trash2 size={12} /> Delete Selected</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                {canManage && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="store-log-select-all"
                      checked={filtered.length > 0 && filtered.every((r) => selected.includes(rowKey(r)))}
                      onChange={() => toggleAll(filtered.map(rowKey))}
                    />
                  </th>
                )}
                <th className="text-left px-6 py-3">S.No</th>
                <th className="text-left px-6 py-3">Item Code</th>
                <th className="text-left px-6 py-3">Item Name</th>
                <th className="text-left px-6 py-3">Category</th>
                <th className="text-left px-6 py-3">Cost Centre</th>
                <th className="text-left px-6 py-3">Unit</th>
                <th className="text-right px-6 py-3">Received</th>
                <th className="text-right px-6 py-3">Issued</th>
                <th className="text-right px-6 py-3">Balance</th>
                {canManage && <th className="text-right px-6 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="indent-log-list">
              {filtered.length === 0 && <tr><td colSpan={canManage ? 11 : 9} className="text-center py-12 text-slate-400">No inventory yet</td></tr>}
              {filtered.map((r) => {
                const k = rowKey(r);
                return (
                  <tr key={k} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`indent-log-row-${r.item_code || r.sn}`}>
                    {canManage && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(k)}
                          onChange={() => toggle(k)}
                          data-testid={`store-log-select-${r.item_code || r.sn}`}
                        />
                      </td>
                    )}
                    <td className="px-6 py-3 font-mono text-xs">{r.sn}</td>
                    <td className="px-6 py-3 font-mono font-semibold">{r.item_code || "—"}</td>
                    <td className="px-6 py-3">{r.description}</td>
                    <td className="px-6 py-3">{r.category || "—"}</td>
                    <td className="px-6 py-3 text-slate-700 max-w-[200px] truncate" title={r.cost_centers || ""}>{r.cost_centers || "—"}</td>
                    <td className="px-6 py-3">{r.unit}</td>
                    <td className="px-6 py-3 text-right font-mono text-emerald-700">{r.received}</td>
                    <td className="px-6 py-3 text-right font-mono text-rose-600">{r.issued}</td>
                    <td className="px-6 py-3 text-right font-mono font-semibold">{r.balance}</td>
                    {canManage && (
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => remove(r)}
                          data-testid={`indent-log-delete-${r.item_code || r.sn}`}
                          title="Remove from Store Log"
                          className="p-1.5 hover:bg-rose-50 rounded text-rose-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
